import React, { useEffect, useMemo, useState } from "react";
import "./LanguagesPage.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useFirebase } from "../contexts/FirebaseContext";
import {
  LANGUAGE_UNITS,
  getLanguageLesson,
  getLessonsForUnit,
} from "../constants/languageLessons";
import { answersMatch } from "../utils/languageAnswers";
import { speakEnglish, canSpeakEnglish } from "../utils/speakEnglish";
import {
  playLanguageClick,
  playLanguageSelect,
  playLanguageCorrect,
  playLanguageWrong,
} from "../utils/languageSounds";

const pick = (value, lang) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value[lang] || value.en || value.de || "";
};

const shuffle = (items) => {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

const LanguagesPage = () => {
  const { t, language } = useLanguage();
  const { addPoints, englishProgress, markEnglishLessonComplete, saveEnglishLearnerName } =
    useFirebase();
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  /** intro | teach | practice | done */
  const [phase, setPhase] = useState("intro");
  const [teachIndex, setTeachIndex] = useState(0);
  const [step, setStep] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [typedValue, setTypedValue] = useState("");
  const [orderBuilt, setOrderBuilt] = useState([]);
  const [orderBank, setOrderBank] = useState([]);
  const [heardSpeak, setHeardSpeak] = useState(false);
  const [checked, setChecked] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [sessionPoints, setSessionPoints] = useState(0);

  const selected = selectedId ? getLanguageLesson(selectedId) : null;
  const teachCards = selected?.teach?.cards || [];
  const exercises = selected?.exercises || [];
  const teachCard = teachCards[teachIndex];
  const exercise = exercises[step];
  const teachTotal = teachCards.length;
  const practiceTotal = exercises.length;
  const speechOk = canSpeakEnglish();

  const sessionProgress = (() => {
    if (!selected) return 0;
    if (phase === "done") return 100;
    const totalUnits = 1 + teachTotal + practiceTotal;
    if (phase === "intro") return 0;
    if (phase === "teach") {
      return Math.round(((1 + teachIndex) / totalUnits) * 100);
    }
    return Math.round(((1 + teachTotal + step) / totalUnits) * 100);
  })();

  const currentAnswer = useMemo(() => {
    if (!exercise) return "";
    if (exercise.type === "order") {
      return orderBuilt.join(" ");
    }
    if (exercise.type === "type") return typedValue;
    if (exercise.type === "speak") return heardSpeak ? exercise.speakText : "";
    return selectedChoice;
  }, [exercise, orderBuilt, typedValue, selectedChoice, heardSpeak]);

  const isCorrect =
    checked &&
    exercise &&
    (exercise.type === "speak"
      ? heardSpeak
      : exercise.type === "type" || exercise.type === "order"
        ? answersMatch(
            currentAnswer,
            exercise.type === "order"
              ? exercise.answerWords.join(" ")
              : exercise.answer,
            { caseSensitive: Boolean(exercise.caseSensitive) }
          )
        : selectedChoice === exercise.answer);

  const resetPracticeStats = () => {
    setCorrectCount(0);
    setWrongCount(0);
    setSessionPoints(0);
  };

  const resetExerciseState = (nextExercise) => {
    setSelectedChoice(null);
    setTypedValue("");
    setHeardSpeak(false);
    setChecked(false);
    if (nextExercise?.type === "order" && nextExercise.answerWords?.length) {
      setOrderBuilt([]);
      setOrderBank(shuffle(nextExercise.answerWords));
    } else {
      setOrderBuilt([]);
      setOrderBank([]);
    }
  };

  useEffect(() => {
    setPhase("intro");
    setTeachIndex(0);
    setStep(0);
    resetExerciseState(null);
    resetPracticeStats();
  }, [selectedId]);

  const hear = (text) => {
    if (!text) return;
    playLanguageClick();
    speakEnglish(text);
  };

  const startLesson = (id) => {
    playLanguageClick();
    setSelectedId(id);
  };

  const exitLesson = () => {
    playLanguageClick();
    setSelectedId(null);
  };

  const handleTeachContinue = () => {
    playLanguageClick();
    if (phase === "intro") {
      setPhase("teach");
      setTeachIndex(0);
      return;
    }
    if (teachIndex >= teachTotal - 1) {
      setPhase("practice");
      setStep(0);
      resetPracticeStats();
      resetExerciseState(exercises[0]);
      return;
    }
    setTeachIndex((current) => current + 1);
  };

  const handleSelectChoice = (choice) => {
    if (checked) return;
    playLanguageSelect();
    setSelectedChoice(choice);
  };

  const handleOrderPick = (word, index) => {
    if (checked) return;
    playLanguageSelect();
    setOrderBank((bank) => bank.filter((_, i) => i !== index));
    setOrderBuilt((built) => [...built, word]);
  };

  const handleOrderRemove = (index) => {
    if (checked) return;
    playLanguageClick();
    const word = orderBuilt[index];
    setOrderBuilt((built) => built.filter((_, i) => i !== index));
    setOrderBank((bank) => [...bank, word]);
  };

  const canCheck = () => {
    if (!exercise || checked) return false;
    if (exercise.type === "choose" || exercise.type === "fill") {
      return Boolean(selectedChoice);
    }
    if (exercise.type === "type") {
      return typedValue.trim().length > 0;
    }
    if (exercise.type === "order") {
      return (
        orderBuilt.length > 0 &&
        orderBuilt.length === (exercise.answerWords?.length || 0)
      );
    }
    if (exercise.type === "speak") {
      return heardSpeak;
    }
    return false;
  };

  const handleCheck = () => {
    if (!canCheck()) return;
    let correct = false;
    if (exercise.type === "speak") {
      correct = heardSpeak;
    } else if (exercise.type === "type") {
      correct = answersMatch(typedValue, exercise.answer, {
        caseSensitive: Boolean(exercise.caseSensitive),
      });
    } else if (exercise.type === "order") {
      correct = answersMatch(orderBuilt.join(" "), exercise.answerWords.join(" "), {
        caseSensitive: true,
      });
    } else {
      correct = selectedChoice === exercise.answer;
    }

    setChecked(true);
    const delta = correct ? 1 : -1;
    if (correct) {
      playLanguageCorrect();
      setCorrectCount((count) => count + 1);
    } else {
      playLanguageWrong();
      setWrongCount((count) => count + 1);
    }
    setSessionPoints((current) => current + delta);
    addPoints(delta);
  };

  const handlePracticeContinue = () => {
    if (!checked && exercise?.type !== "speak") return;
    // Speak: allow continue after check
    if (!checked) return;
    playLanguageClick();
    if (step >= practiceTotal - 1) {
      setPhase("done");
      markEnglishLessonComplete(selected.id, { sessionPoints });
      return;
    }
    const nextStep = step + 1;
    setStep(nextStep);
    resetExerciseState(exercises[nextStep]);
  };

  const handleSpeakHeard = () => {
    hear(exercise.speakText);
    setHeardSpeak(true);
  };

  const handleSpeakConfirm = () => {
    if (!heardSpeak || checked) return;
    // Confirm counts as check for speak exercises
    setSelectedChoice(exercise.speakText);
    setChecked(true);
    playLanguageCorrect();
    setCorrectCount((count) => count + 1);
    setSessionPoints((current) => current + 1);
    addPoints(1);
  };

  const handleRetry = () => {
    playLanguageClick();
    setPhase("intro");
    setTeachIndex(0);
    setStep(0);
    resetExerciseState(null);
    resetPracticeStats();
  };

  const learnerName = String(englishProgress?.learnerName || "").trim();
  const needsName = !learnerName;

  const handleSaveName = async (event) => {
    event.preventDefault();
    if (nameSaving || !nameDraft.trim()) return;
    setNameSaving(true);
    playLanguageClick();
    try {
      await saveEnglishLearnerName(nameDraft);
      setNameDraft("");
    } finally {
      setNameSaving(false);
    }
  };

  const formatPointsDelta = (value) => (value > 0 ? `+${value}` : String(value));

  if (needsName) {
    return (
      <div className="sw-languages-page">
        <div className="sw-languages-name-gate">
          <p className="sw-languages-eyebrow">{t("languagesEyebrow")}</p>
          <h1 className="sw-languages-heading">{t("languagesNameTitle")}</h1>
          <p className="sw-languages-sub">{t("languagesNameSubtitle")}</p>
          <form className="sw-languages-name-form" onSubmit={handleSaveName}>
            <input
              className="sw-languages-type-input"
              type="text"
              value={nameDraft}
              maxLength={40}
              autoComplete="given-name"
              placeholder={t("languagesNamePlaceholder")}
              onChange={(event) => setNameDraft(event.target.value)}
              autoFocus
            />
            <button
              type="submit"
              className="sw-languages-primary"
              disabled={nameSaving || !nameDraft.trim()}
            >
              {t("languagesNameSave")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const correctAnswerDisplay = () => {
    if (!exercise) return "";
    if (exercise.type === "order") return exercise.answerWords.join(" ");
    if (exercise.type === "type") {
      return Array.isArray(exercise.answer) ? exercise.answer[0] : exercise.answer;
    }
    if (exercise.type === "speak") return exercise.speakText;
    return exercise.answer;
  };

  const renderHearButton = (text, label = t("languagesHear")) => {
    if (!text || !speechOk) return null;
    return (
      <button
        type="button"
        className="sw-languages-hear"
        onClick={() => hear(text)}
      >
        {label}
      </button>
    );
  };

  const renderSessionChrome = (progressValue) => (
    <div className="sw-languages-session-top">
      <button
        type="button"
        className="sw-languages-close"
        onClick={exitLesson}
        aria-label={t("languagesQuit")}
      >
        ×
      </button>
      <div
        className="sw-languages-progress-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progressValue}
      >
        <div
          className="sw-languages-progress-fill"
          style={{ width: `${progressValue}%` }}
        />
      </div>
      {phase === "practice" || phase === "done" ? (
        <div
          className={[
            "sw-languages-session-points",
            sessionPoints > 0 ? "is-positive" : "",
            sessionPoints < 0 ? "is-negative" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {formatPointsDelta(sessionPoints)}
        </div>
      ) : null}
    </div>
  );

  if (selected && phase === "done") {
    return (
      <div className="sw-languages-page">
        <div className="sw-languages-session">
          {renderSessionChrome(100)}
          <div className="sw-languages-done">
            <p className="sw-languages-eyebrow">{t("languagesDoneEyebrow")}</p>
            <h1 className="sw-languages-heading">
              {t("languagesDoneTitle").replace("{name}", learnerName)}
            </h1>
            <p className="sw-languages-sub">
              {t("languagesDoneScore")
                .replace("{correct}", String(correctCount))
                .replace("{wrong}", String(wrongCount))
                .replace("{total}", String(practiceTotal))}
            </p>
            <p className="sw-languages-done-points">
              {t("languagesDonePoints").replace(
                "{points}",
                formatPointsDelta(sessionPoints)
              )}
            </p>
            <div className="sw-languages-done-saved">
              <span className="sw-languages-done-box is-filled" aria-hidden="true">
                ✓
              </span>
              <span>{t("languagesLessonDoneSaved")}</span>
            </div>
            <div className="sw-languages-done-actions">
              <button
                type="button"
                className="sw-languages-primary"
                onClick={handleRetry}
              >
                {t("languagesRetry")}
              </button>
              <button
                type="button"
                className="sw-languages-secondary"
                onClick={exitLesson}
              >
                {t("languagesBack")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selected && (phase === "intro" || phase === "teach")) {
    const isIntro = phase === "intro";
    return (
      <div className="sw-languages-page">
        <div className="sw-languages-session">
          {renderSessionChrome(sessionProgress)}

          <div className="sw-languages-teach">
            {isIntro ? (
              <>
                <h1 className="sw-languages-heading">
                  {pick(selected.title, language)}
                </h1>
                <p className="sw-languages-sub">
                  {pick(selected.teach.intro, language)}
                </p>
              </>
            ) : (
              <>
                <p className="sw-languages-phase-label">
                  {teachIndex + 1} / {teachTotal}
                </p>
                <div className="sw-languages-teach-phrase-row">
                  <p className="sw-languages-teach-phrase">{teachCard.phrase}</p>
                  {renderHearButton(teachCard.phrase)}
                </div>
                <p className="sw-languages-teach-meaning">
                  {pick(teachCard.meaning, language)}
                </p>
                {teachCard.example ? (
                  <div className="sw-languages-teach-example">
                    <div className="sw-languages-teach-example-top">
                      <span className="sw-languages-teach-example-label">
                        {t("languagesExample")}
                      </span>
                      {renderHearButton(teachCard.example)}
                    </div>
                    <p>{teachCard.example}</p>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="sw-languages-footer">
            <span />
            <button
              type="button"
              className="sw-languages-primary"
              onClick={handleTeachContinue}
            >
              {isIntro
                ? t("languagesStartLearning")
                : teachIndex >= teachTotal - 1
                  ? t("languagesStartPractice")
                  : t("languagesContinue")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selected && phase === "practice" && exercise) {
    const showChoices =
      exercise.type === "choose" || exercise.type === "fill";

    return (
      <div className="sw-languages-page">
        <div className="sw-languages-session">
          {renderSessionChrome(sessionProgress)}

          <div className="sw-languages-exercise">
            <p className="sw-languages-phase-label">
              {step + 1} / {practiceTotal}
            </p>
            <p className="sw-languages-prompt">
              {pick(exercise.prompt, language)}
            </p>

            {exercise.cue ? (
              <div className="sw-languages-cue-row">
                <p className="sw-languages-cue">{exercise.cue}</p>
                {renderHearButton(exercise.cue)}
              </div>
            ) : null}

            {exercise.type === "fill" ? (
              <p className="sw-languages-sentence">
                {exercise.before ? <span>{exercise.before} </span> : null}
                <span className="sw-languages-blank">
                  {selectedChoice || "___"}
                </span>
                {exercise.after ? <span> {exercise.after}</span> : null}
              </p>
            ) : null}

            {exercise.type === "type" ? (
              <div className="sw-languages-type-wrap">
                <input
                  className={[
                    "sw-languages-type-input",
                    checked ? (isCorrect ? "is-correct" : "is-wrong") : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  type="text"
                  value={typedValue}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  disabled={checked}
                  placeholder={t("languagesTypePlaceholder")}
                  onChange={(event) => setTypedValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (!checked) handleCheck();
                      else handlePracticeContinue();
                    }
                  }}
                />
              </div>
            ) : null}

            {exercise.type === "order" ? (
              <div className="sw-languages-order">
                <div className="sw-languages-order-built">
                  {orderBuilt.length === 0 ? (
                    <span className="sw-languages-order-placeholder">
                      {t("languagesOrderHint")}
                    </span>
                  ) : (
                    orderBuilt.map((word, index) => (
                      <button
                        key={`built-${word}-${index}`}
                        type="button"
                        className="sw-languages-order-chip is-built"
                        onClick={() => handleOrderRemove(index)}
                        disabled={checked}
                      >
                        {word}
                      </button>
                    ))
                  )}
                </div>
                <div className="sw-languages-order-bank">
                  {orderBank.map((word, index) => (
                    <button
                      key={`bank-${word}-${index}`}
                      type="button"
                      className="sw-languages-order-chip"
                      onClick={() => handleOrderPick(word, index)}
                      disabled={checked}
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {exercise.type === "speak" ? (
              <div className="sw-languages-speak">
                <p className="sw-languages-speak-text">{exercise.speakText}</p>
                <div className="sw-languages-speak-actions">
                  <button
                    type="button"
                    className="sw-languages-secondary"
                    onClick={handleSpeakHeard}
                  >
                    {t("languagesHear")}
                  </button>
                  <button
                    type="button"
                    className="sw-languages-primary"
                    onClick={handleSpeakConfirm}
                    disabled={!heardSpeak || checked}
                  >
                    {t("languagesISaidIt")}
                  </button>
                </div>
                {!heardSpeak ? (
                  <p className="sw-languages-speak-hint">
                    {t("languagesSpeakHint")}
                  </p>
                ) : null}
              </div>
            ) : null}

            {showChoices ? (
              <div className="sw-languages-choices">
                {exercise.choices.map((choice) => {
                  const isSelected = selectedChoice === choice;
                  const showCorrect = checked && choice === exercise.answer;
                  const showWrong =
                    checked && isSelected && choice !== exercise.answer;
                  return (
                    <button
                      key={choice}
                      type="button"
                      className={[
                        "sw-languages-choice",
                        isSelected ? "is-selected" : "",
                        showCorrect ? "is-correct" : "",
                        showWrong ? "is-wrong" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => handleSelectChoice(choice)}
                      aria-pressed={isSelected}
                      disabled={checked}
                    >
                      <span className="sw-languages-choice-text">{choice}</span>
                      {isSelected && !checked ? (
                        <span
                          className="sw-languages-choice-mark"
                          aria-hidden="true"
                        >
                          ✓
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div
            className={[
              "sw-languages-footer",
              checked ? (isCorrect ? "is-correct" : "is-wrong") : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {checked ? (
              <p className="sw-languages-feedback">
                {isCorrect
                  ? t("languagesCorrect")
                  : t("languagesWrong").replace(
                      "{answer}",
                      correctAnswerDisplay()
                    )}
              </p>
            ) : (
              <span />
            )}
            {exercise.type === "speak" ? (
              checked ? (
                <button
                  type="button"
                  className="sw-languages-primary"
                  onClick={handlePracticeContinue}
                >
                  {t("languagesContinue")}
                </button>
              ) : (
                <span />
              )
            ) : !checked ? (
              <button
                type="button"
                className="sw-languages-primary"
                onClick={handleCheck}
                disabled={!canCheck()}
              >
                {t("languagesCheck")}
              </button>
            ) : (
              <button
                type="button"
                className="sw-languages-primary"
                onClick={handlePracticeContinue}
              >
                {t("languagesContinue")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sw-languages-page">
      <header className="sw-languages-header">
        <div>
          <p className="sw-languages-eyebrow">{t("languagesEyebrow")}</p>
          <h1 className="sw-languages-heading">{t("languagesTitle")}</h1>
          <p className="sw-languages-sub">{t("languagesSubtitle")}</p>
        </div>
      </header>

      <div className="sw-languages-units">
        {LANGUAGE_UNITS.map((unit) => {
          const lessons = getLessonsForUnit(unit.id);
          return (
            <section key={unit.id} className="sw-languages-unit">
              <h2 className="sw-languages-unit-title">
                {pick(unit.title, language)}
              </h2>
              <ul className="sw-languages-list">
                {lessons.map((item) => {
                  const isDone = Boolean(
                    englishProgress?.lessons?.[item.id]?.done
                  );
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={[
                          "sw-languages-card",
                          isDone ? "is-done" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => startLesson(item.id)}
                      >
                        <span
                          className={[
                            "sw-languages-done-badge",
                            isDone ? "is-complete" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <span
                            className="sw-languages-done-box"
                            aria-hidden="true"
                          >
                            {isDone ? "✓" : ""}
                          </span>
                          <span className="sw-languages-done-label">
                            {t("languagesLessonDone")}
                          </span>
                        </span>
                        <span className="sw-languages-card-title">
                          {pick(item.title, language)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default LanguagesPage;
